import { useStatsigClient } from "@statsig/react-bindings";
import useUserRole from "@/hooks/use-user-role";

/**
 * Statsig gates whose surfaces span more than one feature area, so no single
 * area can own the constant. Area-local gates stay next to the thing they
 * gate — this file is for the ones two areas must agree on.
 */

/**
 * Rollout of the HomeCare plan proposal surfaces: the office scope-tab entry
 * point and the admin "HCP Proposal" module. Both read the same gate so the
 * feature appears and disappears as one thing rather than half-on.
 */
export const HOMECARE_PLAN_PROPOSAL_GATE = "rollout_homecare_plan_proposal";

export function useHomecarePlanProposalGate(): boolean {
  const { checkGate } = useStatsigClient();
  return checkGate(HOMECARE_PLAN_PROPOSAL_GATE);
}

/**
 * Rollout of the order workflow (`features/office/order-workflow`): the button
 * beside an order's status that opens its pipeline diagram, and the Orders ›
 * Workflow tab (`/office/orders/workflow`) with its sidebar row. One
 * key, so the diagram never shows in one place and not the other.
 *
 * On for the beta tier.
 */
export const ORDER_WORKFLOW_GATE = "rollout_order_workflow";

/**
 * `isLoading` is returned for the route guard: a cold Statsig cache reads as "gate
 * off". The button and the sidebar row only hide, so they can ignore it.
 */
export function useOrderWorkflowGate(): {
  enabled: boolean;
  isLoading: boolean;
} {
  const { checkGate, isLoading } = useStatsigClient();
  return { enabled: checkGate(ORDER_WORKFLOW_GATE), isLoading };
}

/**
 * Rollout of the service catalog (`features/office/catalog-new`): the Catalog
 * sidebar row, the `/office/catalog-new` screens (the jobs and the market rates
 * that price them) and the catalog jobs offered by an order's scope search.
 */
export const CATALOG_NEW_GATE = "rollout_catalog_new";

/**
 * `isLoading` is returned for the route guard: a cold Statsig cache reads as "gate
 * off". The sidebar row only hides, so it can ignore it.
 */
export function useCatalogNewGate(): { enabled: boolean; isLoading: boolean } {
  const { checkGate, isLoading } = useStatsigClient();
  return { enabled: checkGate(CATALOG_NEW_GATE), isLoading };
}

/**
 * Rollout of Smart Actions (`features/office/smart-actions`): the sidebar row,
 * the `/office/smart-actions/*` screens, the order card and the composer hint.
 * One key, so the row never points at a screen that bounces its caller.
 *
 * The gate is half of the check: Smart Actions is also for ADMIN and MANAGER
 * only (the platform refuses everyone else). `useSmartActionsAccess()` in the
 * Smart Actions slice combines the two.
 */
export const SMART_ACTIONS_GATE = "rollout_smart_actions";

/**
 * `isLoading` is returned for the route guards, as with `useOfficeInboxGate`: a
 * cold cache reads as "gate off". The sidebar row only hides, so it can
 * ignore it.
 */
export function useSmartActionsGate(): {
  enabled: boolean;
  isLoading: boolean;
} {
  const { checkGate, isLoading } = useStatsigClient();
  return { enabled: checkGate(SMART_ACTIONS_GATE), isLoading };
}

/**
 * Reports › AI feedback (`features/office/reports/ai-feedback`): the sidebar
 * row and the `/office/reports/ai-feedback` screen behind it, which redirects
 * to Order Overview while this is off. Hidden on 2026-10-20 at the owner's
 * request; nothing else changed, so turning it back on is flipping this flag.
 *
 * A build-time constant, not a Statsig gate, for the reason in
 * `notification-surfaces.ts`: it is a product decision to hide a finished
 * screen, not a rollout. Feedback capture in Smart Actions is unaffected.
 */
export const AI_FEEDBACK_REPORT_ENABLED = false;

/**
 * Rollout of the office revenue reports: Reports › Revenue
 * (`features/office/reports/revenue`, `/office/reports/revenue`) and Reports ›
 * HomeCare Renewals (`features/office/reports/homecare-renewals`,
 * `/office/reports/homecare-renewals`), each sidebar row and the screen behind
 * it. One key, so a row never points at a screen that bounces its caller.
 *
 * The gate is half of the check: both reports are also for ADMIN and MANAGER
 * only (their platform routes refuse everyone else).
 * `useRevenueReportAccess()` in the revenue slice and `RenewalsReport` in the
 * renewals slice each combine the two.
 */
export const REVENUE_REPORT_GATE = "rollout_revenue_report";

/**
 * `isLoading` is returned for the route guard: a cold Statsig cache reads as
 * "gate off". The sidebar row only hides, so it can ignore it.
 */
export function useRevenueReportGate(): {
  enabled: boolean;
  isLoading: boolean;
} {
  const { checkGate, isLoading } = useStatsigClient();
  return { enabled: checkGate(REVENUE_REPORT_GATE), isLoading };
}

/**
 * Rollout of the visit monitor (`features/office/vendors/visit-monitor`): the
 * Vendors › Visit monitor sidebar row and the `/office/vendors/visit-monitor`
 * screen behind it. One key, so the row never points at a screen that bounces
 * its caller. The technician's no-login visit link page (`/v/$linkCode`) is
 * never gated: a technician has no Statsig user.
 */
export const VISIT_MONITORING_GATE = "rollout_visit_monitoring";

/**
 * Who may use the visit monitor: the `rollout_visit_monitoring` gate AND the
 * ADMIN or MANAGER role. The platform refuses every other role, so a row shown
 * to them would only open a page of errors. `enabled` is false while either
 * check is loading.
 *
 * `isLoading` is returned for the route guard: a cold Statsig cache reads as
 * "gate off". The sidebar row only hides, so it can ignore it.
 */
export function useVisitMonitoringGate(): {
  enabled: boolean;
  isLoading: boolean;
} {
  const { checkGate, isLoading: isGateLoading } = useStatsigClient();
  const { isAdmin, isManager, isLoading: isRoleLoading } = useUserRole();
  return {
    enabled: checkGate(VISIT_MONITORING_GATE) && (isAdmin || isManager),
    isLoading: isGateLoading || isRoleLoading,
  };
}

/**
 * Finished notification surfaces that are deliberately not exposed yet. Build-time constants
 * rather than Statsig gates — the reasoning is in the module header.
 */
export {
  MEMBER_GLOBAL_CHANNELS_ENABLED,
  MEMBER_QUIET_HOURS_ENABLED,
  NOTIFICATION_INBOX_VIEWS_ENABLED,
  NOTIFICATION_ROW_STATE_ACTIONS_ENABLED,
} from "./notification-surfaces";
